import { lazy, Suspense, useCallback, useRef } from "react";
import { stopCopilotConversation } from "@/features/copilot/api/copilot.api";
import { CopilotConversationBody } from "@/features/copilot/components/copilot-conversation-body";
import {
  CopilotPanelSurface,
  ResizeHandle,
} from "@/features/copilot/components/copilot-panel-surface";
import type { CopilotRuntimeProps } from "@/features/copilot/components/copilot-runtime";
import type { CopilotScopeKey } from "@/features/copilot/lib/copilot-query-keys";
import type { CopilotConversation } from "@/features/copilot/schemas/copilot.schema";
import { useCopilotLayout } from "@/shared/components/layout/copilot-layout.context";
import { SheetContent, SheetTitle } from "@/shared/components/ui/sheet";
import { cn } from "@/shared/lib/cn";

const LazyCopilotRuntime = lazy(() =>
  import("@/features/copilot/components/copilot-runtime").then((module) => ({
    default: module.CopilotRuntime,
  })),
);

type PanelMode = "desktop" | "side-sheet" | "bottom-sheet";

interface Props {
  mode: PanelMode;
  scope: CopilotScopeKey | null;
  conversation: CopilotConversation | null;
  restoreRevision: number;
  onRestoreRequired: CopilotRuntimeProps["onRestoreRequired"];
  onApprovalStarted: NonNullable<CopilotRuntimeProps["onApprovalStarted"]>;
  onApprovalFailed: NonNullable<CopilotRuntimeProps["onApprovalFailed"]>;
  onNewConversation: () => void;
  onRuntimeRunningChange: (running: boolean) => void;
  onAfterStop: () => Promise<void>;
  loading: boolean;
  loadError: boolean;
  createError: boolean;
  scopeUnavailable: boolean;
  restoreError: boolean;
  noCredits: boolean;
}

export function CopilotPanel(props: Props) {
  const layout = useCopilotLayout();
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const stop = useCallback(
    async (conversationId: string) => {
      await stopCopilotConversation(conversationId);
      await props.onAfterStop();
    },
    [props.onAfterStop],
  );

  if (!layout.hasOpened) return null;

  const propsForRuntime =
    props.scope && props.conversation
      ? {
          scope: props.scope,
          conversation: props.conversation,
          restoreRevision: props.restoreRevision,
          onRestoreRequired: props.onRestoreRequired,
          onApprovalStarted: props.onApprovalStarted,
          onApprovalFailed: props.onApprovalFailed,
          onStop: stop,
          runtimeKey: JSON.stringify([
            props.scope.accountId,
            props.scope.userId,
            props.scope.workspaceId,
            props.conversation.id,
            props.restoreRevision,
          ]),
        }
      : null;

  const runtimeContent = propsForRuntime ? (
    <CopilotConversationBody
      key={propsForRuntime.runtimeKey}
      conversation={props.conversation}
      composerRef={composerRef}
      noCredits={props.noCredits}
      onRuntimeRunningChange={props.onRuntimeRunningChange}
    />
  ) : null;

  const surface = (
    <CopilotPanelSurface
      {...props}
      conversation={props.conversation}
      runtimeProps={propsForRuntime}
      runtimeContent={runtimeContent}
    />
  );

  const panelSurface =
    props.mode === "desktop" ? (
      // Overlays the page rather than taking a grid column, so opening it never reflows the
      // page under it. No scrim: the page stays usable, it is what the conversation is about.
      <aside
        aria-hidden={!layout.isOpen}
        id="copilot-panel"
        className={cn(
          "fixed inset-y-0 right-0 z-40 flex min-h-0 flex-col bg-background transition-[translate,width,visibility] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] has-[[data-resizing]]:transition-none motion-reduce:transition-none",
          layout.isOpen ? "visible translate-x-0" : "invisible translate-x-full",
          layout.isExpanded ? "w-full" : "border-l border-border shadow-xl",
        )}
        style={layout.isExpanded ? undefined : { width: layout.width }}
      >
        {layout.isExpanded ? null : <ResizeHandle />}
        {surface}
      </aside>
    ) : (
      <SheetContent
        id="copilot-panel"
        side={props.mode === "bottom-sheet" ? "bottom" : "right"}
        showCloseButton={false}
        onOpenAutoFocus={(event) => event.preventDefault()}
        // The window Escape handler steps full page down to the sheet; keep Radix from closing it too.
        onEscapeKeyDown={(event) => {
          if (layout.isExpanded) event.preventDefault();
        }}
        className={cn(
          "gap-0 p-0",
          props.mode === "bottom-sheet"
            ? layout.isExpanded
              ? "h-dvh max-h-dvh pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
              : "h-[min(82dvh,calc(100dvh-env(safe-area-inset-top)-1rem))] max-h-[calc(100dvh-env(safe-area-inset-top)-1rem)] rounded-t-2xl pb-[env(safe-area-inset-bottom)]"
            : cn(
                "h-svh max-w-none sm:max-w-none",
                layout.isExpanded ? "w-full" : "w-[min(92vw,29rem)]",
              ),
        )}
      >
        <SheetTitle className="sr-only">Hand</SheetTitle>
        {surface}
      </SheetContent>
    );
  if (!propsForRuntime) return panelSurface;

  const runtimeLoadingSurface = (
    <CopilotPanelSurface
      {...props}
      conversation={props.conversation}
      runtimeProps={null}
      runtimeContent={null}
    />
  );
  return (
    <Suspense fallback={runtimeLoadingSurface}>
      <LazyCopilotRuntime {...propsForRuntime}>{panelSurface}</LazyCopilotRuntime>
    </Suspense>
  );
}
